import React, { useState, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Upload, Trash2, Crop, Save, X } from 'lucide-react';
import { BASE_URL } from '@/config/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

interface StudentAvatarModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentId: string;
  currentAvatarUrl: string;
  onAvatarUpdate: (newAvatarUrl: string) => void;
  onStudentRefresh?: () => void;
}

export const StudentAvatarModal: React.FC<StudentAvatarModalProps> = ({
  isOpen,
  onClose,
  studentId,
  currentAvatarUrl,
  onAvatarUpdate,
  onStudentRefresh,
}) => {
  const { toast } = useToast();
  const { token } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [showCropper, setShowCropper] = useState(false);
  const [croppedImage, setCroppedImage] = useState<Blob | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useLanguage();

  // Crop state - now using size instead of width/height for circular crop
  const [crop, setCrop] = useState({ x: 0, y: 0, size: 200 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setSelectedImage(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target?.result as string);
          setShowCropper(true);
        };
        reader.readAsDataURL(file);
      } else {
        toast({
          title: 'Invalid file type',
          description: 'Please select an image file.',
          variant: 'destructive',
        });
      }
    }
  };

  const handleImageLoad = () => {
    if (imageRef.current && containerRef.current) {
      const img = imageRef.current;
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      
      // Set initial crop size to be larger (40% of smallest dimension)
      const minDimension = Math.min(img.naturalWidth, img.naturalHeight);
      const cropSize = minDimension * 0.4;
      
      setCrop({
        x: (img.naturalWidth - cropSize) / 2,
        y: (img.naturalHeight - cropSize) / 2,
        size: cropSize,
      });
    }
  };

  const getRelativeCoordinates = (e: React.MouseEvent | MouseEvent | React.TouchEvent | TouchEvent) => {
    if (!containerRef.current || !imageRef.current) return { x: 0, y: 0 };
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const img = imageRef.current;
    
    const scaleX = img.naturalWidth / containerRect.width;
    const scaleY = img.naturalHeight / containerRect.height;
    
    // Handle both mouse and touch events
    const clientX = 'touches' in e ? e.touches[0]?.clientX || e.changedTouches[0]?.clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0]?.clientY || e.changedTouches[0]?.clientY : e.clientY;
    
    return {
      x: (clientX - containerRect.left) * scaleX,
      y: (clientY - containerRect.top) * scaleY
    };
  };

  const handlePointerDown = (e: React.MouseEvent | React.TouchEvent, action: 'drag' | 'resize') => {
    e.preventDefault();
    e.stopPropagation();
    
    const coords = getRelativeCoordinates(e);
    
    if (action === 'drag') {
      setIsDragging(true);
      setDragStart({ x: coords.x - crop.x, y: coords.y - crop.y });
    } else if (action === 'resize') {
      setIsResizing(true);
      setDragStart(coords);
    }
  };

  const handlePointerMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!imageRef.current || (!isDragging && !isResizing)) return;
    
    const coords = getRelativeCoordinates(e);
    const img = imageRef.current;
    
    if (isDragging) {
      const newX = Math.max(0, Math.min(coords.x - dragStart.x, img.naturalWidth - crop.size));
      const newY = Math.max(0, Math.min(coords.y - dragStart.y, img.naturalHeight - crop.size));
      
      setCrop(prev => ({ ...prev, x: newX, y: newY }));
    } else if (isResizing) {
      const centerX = crop.x + crop.size / 2;
      const centerY = crop.y + crop.size / 2;
      
      // Calculate distance from center to get new radius/size
      const distance = Math.sqrt(
        Math.pow(coords.x - centerX, 2) + Math.pow(coords.y - centerY, 2)
      );
      
      const newSize = Math.max(50, Math.min(distance * 2, 
        Math.min(img.naturalWidth - crop.x, img.naturalHeight - crop.y, 
                 crop.x + crop.size, crop.y + crop.size) * 2));
      
      // Adjust position to keep circle centered
      const newX = Math.max(0, Math.min(centerX - newSize / 2, img.naturalWidth - newSize));
      const newY = Math.max(0, Math.min(centerY - newSize / 2, img.naturalHeight - newSize));
      
      setCrop({ x: newX, y: newY, size: newSize });
    }
  }, [isDragging, isResizing, dragStart, crop]);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
  }, []);

  React.useEffect(() => {
    if (isDragging || isResizing) {
      // Add both mouse and touch event listeners
      document.addEventListener('mousemove', handlePointerMove);
      document.addEventListener('mouseup', handlePointerUp);
      document.addEventListener('touchmove', handlePointerMove, { passive: false });
      document.addEventListener('touchend', handlePointerUp);
      
      return () => {
        document.removeEventListener('mousemove', handlePointerMove);
        document.removeEventListener('mouseup', handlePointerUp);
        document.removeEventListener('touchmove', handlePointerMove);
        document.removeEventListener('touchend', handlePointerUp);
      };
    }
  }, [isDragging, isResizing, handlePointerMove, handlePointerUp]);

  const getCroppedImage = (): Promise<Blob> => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current!;
      const ctx = canvas.getContext('2d')!;
      const img = imageRef.current!;
      
      canvas.width = 200;
      canvas.height = 200;
      
      // Create circular clipping path
      ctx.beginPath();
      ctx.arc(100, 100, 100, 0, 2 * Math.PI);
      ctx.clip();
      
      // Draw the cropped image
      ctx.drawImage(
        img,
        crop.x, crop.y, crop.size, crop.size,
        0, 0, 200, 200
      );
      
      canvas.toBlob((blob) => {
        resolve(blob!);
      }, 'image/jpeg', 0.9);
    });
  };

  const handleCropConfirm = async () => {
    const croppedBlob = await getCroppedImage();
    setCroppedImage(croppedBlob);
    setShowCropper(false);
  };

  const handleUpload = async () => {
    if (!croppedImage) {
      console.error("No cropped image available");
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', croppedImage, 'avatar.jpg');
      formData.append('studentId', studentId);

      const response = await fetch(`${BASE_URL}/api/student/update/avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload avatar');
      }

      toast({
        title: t('profile.avatarUpdated'),
        description: t('profile.avatarUpdatedDesc'),
      });

      onAvatarUpdate(data.avatar_url || data.profile_picture);
      onStudentRefresh?.();
      onClose();
      resetModal();
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Failed to upload avatar',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    setIsUploading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/student/delete/avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ studentId: studentId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete avatar');
      }

      toast({
        title: t('profile.avatarDeleted'),
        description: t('profile.avatarDeletedDesc'),
      });

      onAvatarUpdate(null);
      onStudentRefresh?.();
      onClose();
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error instanceof Error ? error.message : 'Failed to delete avatar',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const resetModal = () => {
    setSelectedImage(null);
    setImagePreview('');
    setShowCropper(false);
    setCroppedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className='mb-3'>{t('profile.manageProfileAvatar')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!showCropper && !croppedImage && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1"
                  variant="outline"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {currentAvatarUrl ? t('profile.changeAvatar') : t('profile.uploadAvatar')}
                </Button>
                
                {currentAvatarUrl && (
                  <Button 
                    onClick={handleDelete}
                    variant="destructive"
                    disabled={isUploading}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}

          {showCropper && (
            <div className="space-y-4">
              <div 
                ref={containerRef}
                className="relative max-w-full overflow-hidden touch-none"
                style={{ userSelect: 'none' }}
              >
                <img
                  ref={imageRef}
                  src={imagePreview}
                  alt="Preview"
                  className="max-w-full h-auto block"
                  onLoad={handleImageLoad}
                  draggable={false}
                />
                
                {/* Circular crop overlay */}
                <div
                  className="absolute border-2 border-white shadow-lg cursor-move bg-black bg-opacity-20 rounded-full"
                  style={{
                    left: `${(crop.x / (imageRef.current?.naturalWidth || 1)) * 100}%`,
                    top: `${(crop.y / (imageRef.current?.naturalHeight || 1)) * 100}%`,
                    width: `${(crop.size / (imageRef.current?.naturalWidth || 1)) * 100}%`,
                    height: `${(crop.size / (imageRef.current?.naturalHeight || 1)) * 100}%`,
                  }}
                  onMouseDown={(e) => handlePointerDown(e, 'drag')}
                  onTouchStart={(e) => handlePointerDown(e, 'drag')}
                >
                  {/* Resize handle - positioned at bottom-right of circle */}
                  <div
                    className="absolute w-4 h-4 bg-white border-2 border-gray-400 rounded-full cursor-se-resize"
                    style={{
                      right: '-8px',
                      bottom: '-8px',
                    }}
                    onMouseDown={(e) => handlePointerDown(e, 'resize')}
                    onTouchStart={(e) => handlePointerDown(e, 'resize')}
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleCropConfirm} className="flex-1">
                  <Crop className="w-4 h-4 mr-2" />
                  Crop Image
                </Button>
                <Button onClick={() => setShowCropper(false)} variant="outline">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {croppedImage && (
            <div className="space-y-4">
              <div className="text-center">
                <img
                  src={URL.createObjectURL(croppedImage)}
                  alt="Cropped preview"
                  className="w-32 h-32 rounded-full mx-auto object-cover"
                />
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={handleUpload}
                  disabled={isUploading}
                  className="flex-1"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isUploading ? 'Uploading...' : 'Save Avatar'}
                </Button>
                <Button onClick={() => setCroppedImage(null)} variant="outline">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        <canvas ref={canvasRef} className="hidden" />
      </DialogContent>
    </Dialog>
  );
};
